import React, { Fragment, useState } from "react";
import PropTypes from "prop-types";
import { graphql } from "gatsby";
import { isArray } from "lodash";

import { Disclosure, RadioGroup, Dialog, Transition } from "@headlessui/react";
import {
  MinusSmallIcon,
  PlusSmallIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import { XCircleIcon } from "@heroicons/react/24/solid";

import { classNames } from "@/helpers/classNames";

import { StoreContext } from "@/context/store-context";
import productOptionsConfig from "@/data/product-options.config";

import SlickSlider from "@/components/slick-slider";
import { getFancyPriceString } from "@/components/price-utils";
import MediaWrapper from "@/components/mediaWrapper";
import LinkWrapper from "@/components/linkWrapper";
import MarkdownWrapper from "@/components/markdownWrapper";

export const query = graphql`
  fragment ShopProductDetailWithImageGridProcessedProduct on ShopifyProcessedProduct {
    shopifyId
    locale
    title
    tags
    descriptionHtml
    options {
      name
      values
      position
    }
    media {
      ...ShopifyMediaSubquery
    }
    variants {
      shopifyId
      displayName
      selectedOptions {
        name
        value
      }
      inventoryQuantity
      presentmentPrices {
        ...ShopifyProductVariantPricePairSubquery
      }
      media {
        ...ShopifyMediaSubquery
      }
    }
    customMetafields
  }

  fragment ShopProductDetailWithImageGridData on StrapiComponentShopProductDetailWithImageGrid {
    closeMenuText
    priceSectionTitle
    optionsTitleSuffix
    addToCartButtonText
    outOfStockButtonText
    sizeTableButtonText
    additionalDetailsTitle
    personalizeButtonText
    personalizeStatusTextFalse
    personalizeStatusTextTrue
    personalizeDialogTitle
    personalizeConfirmButtonText
    personalizeCancelButtonText
    personalizeDeleteButtonText
    personalizeOptionsTitle
    personalizeOptions {
      string
    }
    personalizeCustomTextTitle
    personalizeCustomTextPlaceholder
    personalizeCustomTextHint
  }
`;

function getFontName(font) {
  font = font.toLowerCase().replaceAll(" ", "");
  if (Object.keys(productOptionsConfig?.fontMap).includes(font)) {
    return productOptionsConfig?.fontMap[font];
  }
  return null;
}

function checkPersonalizeCustomTextInput(value) {
  return value && value.length <= 12;
}

function WithImageGrid({ data, products }) {
  if (!data || products?.length !== 1) {
    return <></>;
  }
  const [productData] = products;

  const validateSelectedOptions = (selectedOptions) => {
    if (!isArray(selectedOptions)) {
      return [];
    }

    const validSelectedOptions = [];
    for (let selectedOption of selectedOptions || []) {
      if (
        productData?.options?.find(
          (option) =>
            option.name === selectedOption.name &&
            option.values?.find((value) => value === selectedOption.value)
        )
      ) {
        validSelectedOptions.push(selectedOption);
      }
    }
    return validSelectedOptions.sort((a, b) => {
      let positionA =
        productData.options?.find((e) => e.name === a.name)?.position ||
        Infinity ||
        [];
      let positionB =
        productData.options?.find((e) => e.name === b.name)?.position ||
        Infinity ||
        [];
      return positionA - positionB;
    });
  };

  const getCustomAttributes = () => {
    if (!personalizeCustomText) {
      return [];
    }
    return [
      { key: "customText", value: personalizeCustomText.text },
      { key: "customTextFont", value: personalizeCustomText.font },
    ];
  };

  const changeSelectedOptions = (changedOptions) => {
    const newOptions = [
      ...changedOptions.filter((option) => isProductOption(option)),
    ];
    for (let option of selectedOptions) {
      if (!newOptions.find((newOption) => newOption.name === option.name)) {
        newOptions.push(option);
      }
    }
    const validNewOptions = validateSelectedOptions(newOptions);
    setSelectedOptions(validNewOptions);
  };

  const isProductOption = (option) => {
    return Boolean(
      productData?.options.find(
        (productOption) =>
          productOption.name === option?.name &&
          productOption.values.includes(option?.value)
      )
    );
  };

  const matchesAllOptions = (optionsSetA, optionsSetB) => {
    for (let optionB of optionsSetB || []) {
      if (
        !optionsSetA.find(
          (optionA) =>
            optionA.name === optionB.name && optionA.value === optionB.value
        )
      ) {
        return false;
      }
    }
    return true;
  };

  const findSelectedVariant = (selectedOptions) => {
    let possibleVariants = productData?.variants?.filter((variant) =>
      matchesAllOptions(variant.selectedOptions, selectedOptions)
    );
    return (
      possibleVariants?.find((variant) => variant.inventoryQuantity > 0) ||
      possibleVariants.shift() ||
      [...(productData?.variants || [])].shift()
    );
  };

  const isSelectedValue = (optionName, optionValue) => {
    const option =
      selectedVariant?.selectedOptions?.find(
        (option) => option.name === optionName
      ) || null;
    return option?.value === optionValue;
  };

  const hasAvailableChildVariants = (optionName, optionValue) => {
    const childVariants = getChildVariants(optionName, optionValue);
    const quantities = childVariants.map(
      (variant) => variant.inventoryQuantity
    );
    return quantities.length > 0
      ? quantities.reduce((acc, curr) => acc || curr > 0, false)
      : null;
  };

  const getChildVariants = (optionName, optionValue) => {
    const parentOptions = [];
    for (let { name, values } of productData.options?.sort(
      (a, b) => (a.position || Infinity) - (b.position || Infinity)
    ) || []) {
      if (optionName === name) {
        break;
      }
      let { value } =
        selectedVariant?.selectedOptions?.find(
          (selectedOption) => selectedOption.name === name
        ) || {};
      if (!value || (values?.length && !values.find((e) => e === value))) {
        break;
      }
      parentOptions.push({ name, value });
    }
    parentOptions.push({ name: optionName, value: optionValue });

    return productData.variants?.filter((variant) => {
      for (let parentOption of parentOptions) {
        let variantOption = variant.selectedOptions.find(
          (variantOption) => variantOption.name === parentOption.name
        );
        if (!variantOption || variantOption?.value !== parentOption.value) {
          return false;
        }
      }
      return true;
    });
  };

  const { addVariantToCart, loading } = React.useContext(StoreContext);

  const [isPersonalizable] = useState(
    Boolean(
      productData?.tags?.find((tag) =>
        productOptionsConfig?.personalizeTags.includes(tag)
      )
    )
  );
  const [personalizeOptions] = useState(
    isPersonalizable &&
      data?.personalizeOptions
        ?.map((e) => e.string)
        .filter((e) => getFontName(e))
  );

  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [personalizeCustomTextInput, setPersonalizeCustomTextInput] =
    useState("");
  const [personalizeCustomTextFont, setPersonalizeCustomTextFont] = useState(
    personalizeOptions?.length ? personalizeOptions[0] : null
  );
  const [personalizeCustomText, setPersonalizeCustomText] = useState(null);

  const [selectedOptions, setSelectedOptions] = useState([]);

  const usedOptionNames = selectedOptions.map((option) => option.name);
  const nextMissingOptionName = productData?.options?.find(
    (option) => !usedOptionNames.includes(option.name)
  )?.name;

  const selectedVariant = findSelectedVariant(selectedOptions);

  const descriptionChapters = [];
  if (typeof document !== `undefined` && productData?.descriptionHtml) {
    let el = document.createElement("html");
    el.innerHTML = productData?.descriptionHtml || "";
    let bodies = el.getElementsByTagName("body");
    if (bodies?.length) {
      let list = bodies[0]?.firstElementChild || null;
      if (list?.tagName === "UL") {
        for (let item of list?.children || []) {
          let body = "";
          let next = item?.firstElementChild || null;
          let title = next?.innerHTML || null;
          if (title) {
            while ((next = next?.nextElementSibling)) {
              body += next?.outerHTML || "";
            }
            descriptionChapters.push({ title, body });
          }
        }
      }
    }
  }

  const radioGroupColor = (option, index) => {
    return (
      <RadioGroup
        value={null}
        onChange={changeSelectedOptions}
        className="mt-2"
      >
        <RadioGroup.Label className="sr-only">
          {`${option.name || `option_${index}`}${
            data.optionsTitleSuffix ? ` ${data.optionsTitleSuffix}` : ""
          }`}
        </RadioGroup.Label>
        <span className="grid grid-cols-3 gap-4 xs:grid-cols-5 sm:grid-cols-8 lg:grid-cols-4 xl:grid-cols-5">
          {Boolean(option.values?.length) &&
            option.values.map(
              (value, valueIndex) =>
                Object.keys(productOptionsConfig?.colorMap).includes(value) && (
                  <RadioGroup.Option
                    key={valueIndex}
                    value={[{ name: option.name, value }]}
                    disabled={
                      !(
                        hasAvailableChildVariants(option.name, value) &&
                        (usedOptionNames.includes(option.name) ||
                          nextMissingOptionName === null ||
                          nextMissingOptionName === option.name)
                      )
                    }
                    className={() =>
                      classNames(
                        !usedOptionNames.includes(option.name) &&
                          nextMissingOptionName !== null &&
                          nextMissingOptionName !== option.name
                          ? "opacity-40"
                          : usedOptionNames.includes(option.name) &&
                              isSelectedValue(option.name, value)
                            ? "ring ring-ring-primary"
                            : hasAvailableChildVariants(option.name, value)
                              ? "cursor-pointer hover:ring hover:ring-ring-primary-light"
                              : "",
                        "relative mx-auto flex aspect-[1/1] w-14 items-center justify-center overflow-hidden rounded-full border border-border-light-gray ring-offset-2 focus:outline-none"
                      )
                    }
                  >
                    <RadioGroup.Label as="span" className="sr-only">
                      {value}
                      {hasAvailableChildVariants(option.name, value)
                        ? ""
                        : ` - ${data.outOfStockButtonText}`}
                    </RadioGroup.Label>
                    <svg
                      className={classNames(
                        (usedOptionNames.includes(option.name) ||
                          nextMissingOptionName === option.name) &&
                          !hasAvailableChildVariants(option.name, value)
                          ? "opacity-20"
                          : "",
                        "absolute inset-0 h-full w-full"
                      )}
                      viewBox="0 0 100 100"
                      preserveAspectRatio="none"
                    >
                      <rect
                        fill={
                          Array.isArray(productOptionsConfig?.colorMap[value])
                            ? productOptionsConfig?.colorMap[value][0]
                            : productOptionsConfig?.colorMap[value]
                        }
                        width="100"
                        height="100"
                        x="0"
                        y="0"
                      />
                      <rect
                        fill={
                          Array.isArray(productOptionsConfig?.colorMap[value])
                            ? productOptionsConfig?.colorMap[value][1]
                            : productOptionsConfig?.colorMap[value]
                        }
                        width="60"
                        height="120"
                        x="-8"
                        y="-65"
                        transform="rotate(45)"
                      />
                      <rect
                        fill={
                          Array.isArray(productOptionsConfig?.colorMap[value])
                            ? productOptionsConfig?.colorMap[value][2]
                            : productOptionsConfig?.colorMap[value]
                        }
                        width="60"
                        height="120"
                        x="88"
                        y="-65"
                        transform="rotate(45)"
                      />
                    </svg>
                    <svg
                      className={classNames(
                        (usedOptionNames.includes(option.name) ||
                          nextMissingOptionName === option.name) &&
                          !hasAvailableChildVariants(option.name, value)
                          ? "block"
                          : "hidden",
                        "absolute inset-0 h-full w-full rounded-full border border-border-primary stroke-[4px] text-text-primary"
                      )}
                      viewBox="0 0 100 100"
                      preserveAspectRatio="none"
                      stroke="currentColor"
                    >
                      <line
                        x1={0}
                        y1={100}
                        x2={100}
                        y2={0}
                        vectorEffect="non-scaling-stroke"
                      />
                    </svg>
                  </RadioGroup.Option>
                )
            )}
        </span>
      </RadioGroup>
    );
  };

  const radioGroupText = (option, index) => {
    return (
      <RadioGroup
        value={null}
        onChange={changeSelectedOptions}
        className="mt-2"
      >
        <RadioGroup.Label className="sr-only">
          {`${option.name || `option_${index}`}${
            data.optionsTitleSuffix ? ` ${data.optionsTitleSuffix}` : ""
          }`}
        </RadioGroup.Label>
        <span className="grid grid-cols-3 gap-2 xs:grid-cols-5 sm:grid-cols-6 lg:grid-cols-5 xl:grid-cols-6">
          {Boolean(option.values?.length) &&
            option.values.map((value, valueIndex) => (
              <RadioGroup.Option
                key={valueIndex}
                value={[{ name: option.name, value }]}
                disabled={
                  !(
                    hasAvailableChildVariants(option.name, value) &&
                    (usedOptionNames.includes(option.name) ||
                      nextMissingOptionName === null ||
                      nextMissingOptionName === option.name)
                  )
                }
                className={() =>
                  classNames(
                    !usedOptionNames.includes(option.name) &&
                      nextMissingOptionName !== null &&
                      nextMissingOptionName !== option.name
                      ? "opacity-40"
                      : hasAvailableChildVariants(option.name, value)
                        ? "cursor-pointer hover:ring-2 hover:ring-ring-primary"
                        : "",
                    usedOptionNames.includes(option.name) &&
                      isSelectedValue(option.name, value)
                      ? "bg-background-secondary-light"
                      : "",
                    "relative mb-1 mr-1 flex h-12 w-full items-center justify-center overflow-hidden rounded-sm border border-border-primary focus:outline-none"
                  )
                }
              >
                <RadioGroup.Label as="span" className="sr-only">
                  {value}
                  {hasAvailableChildVariants(option.name, value)
                    ? ""
                    : ` - ${data.outOfStockButtonText}`}
                </RadioGroup.Label>
                <span
                  aria-hidden="true"
                  className={classNames(
                    (usedOptionNames.includes(option.name) ||
                      nextMissingOptionName === option.name) &&
                      !hasAvailableChildVariants(option.name, value)
                      ? "opacity-40"
                      : "",
                    "relative h-8 px-2 py-1"
                  )}
                >
                  {value}
                </span>
                <svg
                  className={classNames(
                    (usedOptionNames.includes(option.name) ||
                      nextMissingOptionName === option.name) &&
                      !hasAvailableChildVariants(option.name, value)
                      ? "block"
                      : "hidden",
                    "absolute inset-0 h-full w-full stroke-2 text-text-primary"
                  )}
                  viewBox="0 0 100 100"
                  preserveAspectRatio="none"
                  stroke="currentColor"
                >
                  <line
                    x1={0}
                    y1={100}
                    x2={100}
                    y2={0}
                    vectorEffect="non-scaling-stroke"
                  />
                </svg>
              </RadioGroup.Option>
            ))}
        </span>
      </RadioGroup>
    );
  };

  return (
    <div className="bg-white">
      {isPersonalizable && data.personalizeButtonText && (
        <Transition.Root show={isDialogOpen} as={Fragment}>
          <Dialog as="div" onClose={setIsDialogOpen} className="relative z-50">
            {/* backdrop */}
            <Transition.Child
              as={Fragment}
              enter="transition-opacity ease-linear duration-300"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="transition-opacity ease-linear duration-300"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <div className="fixed inset-0 bg-black bg-opacity-40" />
            </Transition.Child>

            {/* dialog content */}
            <div className="fixed inset-4 z-40 flex items-center lg:inset-8">
              <Transition.Child
                as={Fragment}
                enter="transition-opacity ease-linear duration-300"
                enterFrom="opacity-0"
                enterTo="opacity-100"
                leave="transition-opacity ease-linear duration-300"
                leaveFrom="opacity-100"
                leaveTo="opacity-0"
              >
                <Dialog.Panel className="relative mx-auto max-h-full max-w-2xl overflow-scroll rounded-md bg-white p-4 pt-12 sm:p-6 sm:pt-14 lg:max-w-7xl lg:p-8">
                  {data.personalizeDialogTitle && (
                    <Dialog.Title as="span" className="sr-only">
                      {data.personalizeDialogTitle}
                    </Dialog.Title>
                  )}
                  <button
                    type="button"
                    onClick={() => {
                      setIsDialogOpen(false);
                    }}
                    className="absolute right-2 top-2 z-50 flex h-8 w-12 items-center justify-center rounded-sm hover:bg-background-secondary-light"
                  >
                    {data.closeMenuText && (
                      <label className="sr-only">{data.closeMenuText}</label>
                    )}
                    <XMarkIcon className="h-6 w-6" />
                  </button>
                  <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
                    <div className="relative col-span-1 aspect-[1/1] w-full overflow-hidden rounded-sm bg-background-secondary-light lg:col-span-2">
                      {Boolean(selectedVariant?.media?.length >= 1) && (
                        <MediaWrapper
                          media={selectedVariant?.media[0]}
                          divClassName="relative aspect-[1/1] w-full"
                        />
                      )}
                      <div className="absolute inset-0 z-50 flex items-center justify-center">
                        <svg className="h-full w-full" viewBox="0 0 1000 1000">
                          <rect
                            x="0"
                            y="0"
                            width="1000"
                            height="1000"
                            fill="transparent"
                          />
                          <text
                            x="50%"
                            y="50%"
                            dominantBaseline="middle"
                            textAnchor="middle"
                            fontSize="2rem"
                            style={{
                              fontFamily: getFontName(
                                personalizeCustomTextFont
                              ),
                            }}
                          >
                            {personalizeCustomTextInput ||
                              data.personalizeCustomTextPlaceholder ||
                              ""}
                          </text>
                        </svg>
                      </div>
                    </div>
                    <div className="mt-0 lg:mt-4">
                      <div className="grid grid-cols-1 gap-8 pt-4 lg:pt-0">
                        {data.personalizeDialogTitle && (
                          <div className="flex flex-wrap text-3xl text-text-primary">
                            <h2 className="font-bold">
                              {data.personalizeDialogTitle}
                            </h2>
                          </div>
                        )}
                        {data.personalizeOptionsTitle &&
                          Boolean(personalizeOptions?.length) && (
                            <div>
                              <h2 className="flex flex-wrap text-sm font-bold text-text-primary">
                                {data.personalizeOptionsTitle}
                              </h2>
                              <RadioGroup
                                value={personalizeCustomTextFont}
                                onChange={setPersonalizeCustomTextFont}
                                className="mt-2"
                              >
                                <RadioGroup.Label className="sr-only">
                                  {data.personalizeOptionsTitle}
                                </RadioGroup.Label>
                                <span className="grid grid-cols-2 gap-2 sm:grid-cols-4 lg:grid-cols-2">
                                  {personalizeOptions.map((value, index) => (
                                    <RadioGroup.Option
                                      key={index}
                                      value={value}
                                      className={() =>
                                        classNames(
                                          personalizeCustomTextFont === value
                                            ? "bg-background-secondary-light"
                                            : "",
                                          "flex min-h-[4rem] w-full cursor-pointer items-center justify-center rounded-sm border border-border-primary hover:ring-2 hover:ring-ring-primary focus:outline-none"
                                        )
                                      }
                                    >
                                      <RadioGroup.Label
                                        as="span"
                                        className="sr-only"
                                      >
                                        {value}
                                      </RadioGroup.Label>
                                      <span
                                        aria-hidden="true"
                                        className={classNames(
                                          "px-4 py-2 text-center text-sm"
                                        )}
                                        style={{
                                          fontFamily: getFontName(value),
                                        }}
                                      >
                                        {value}
                                      </span>
                                    </RadioGroup.Option>
                                  ))}
                                </span>
                              </RadioGroup>
                            </div>
                          )}
                        {data.personalizeCustomTextTitle &&
                          data.personalizeCustomTextPlaceholder && (
                            <div>
                              <label
                                htmlFor="email"
                                className="flex flex-wrap text-sm font-bold text-text-primary"
                              >
                                {data.personalizeCustomTextTitle}
                              </label>
                              <div className="mt-4">
                                <input
                                  type="email"
                                  name="email"
                                  id="email"
                                  className="block w-full rounded-sm border-border-primary bg-background-white shadow-sm focus:outline-none focus:ring-ring-primary sm:text-sm"
                                  placeholder={
                                    data.personalizeCustomTextPlaceholder
                                  }
                                  value={personalizeCustomTextInput}
                                  onChange={(event) => {
                                    setPersonalizeCustomTextInput(
                                      event?.target?.value
                                    );
                                  }}
                                />
                              </div>
                            </div>
                          )}
                        <div className="grid grid-cols-3 gap-2">
                          {data.personalizeCancelButtonText && (
                            <button
                              type="button"
                              onClick={() => {
                                setIsDialogOpen(false);
                              }}
                              className={classNames(
                                "flex flex-1 items-center justify-center rounded-sm border border-border-primary px-8 py-3 text-base font-medium text-text-primary hover:ring-2 hover:ring-ring-primary focus:outline-none sm:w-full"
                              )}
                            >
                              {data.personalizeCancelButtonText}
                            </button>
                          )}
                          {data.personalizeDeleteButtonText && (
                            <button
                              type="button"
                              onClick={() => {
                                setPersonalizeCustomText(null);
                                setIsDialogOpen(false);
                              }}
                              disabled={!personalizeCustomText}
                              className={classNames(
                                !personalizeCustomText
                                  ? "opacity-40"
                                  : "hover:ring-2 hover:ring-ring-primary",
                                "flex flex-1 items-center justify-center rounded-sm border border-border-primary px-8 py-3 text-base font-medium text-text-primary focus:outline-none sm:w-full"
                              )}
                            >
                              {data.personalizeDeleteButtonText}
                            </button>
                          )}
                          {data.personalizeConfirmButtonText && (
                            <button
                              type="button"
                              onClick={() => {
                                setPersonalizeCustomText({
                                  text: personalizeCustomTextInput,
                                  font: personalizeCustomTextFont,
                                });
                                setIsDialogOpen(false);
                              }}
                              disabled={
                                !checkPersonalizeCustomTextInput(
                                  personalizeCustomTextInput
                                )
                              }
                              className={classNames(
                                !checkPersonalizeCustomTextInput(
                                  personalizeCustomTextInput
                                )
                                  ? "opacity-40"
                                  : "hover:bg-background-secondary",
                                "flex flex-1 items-center justify-center rounded-sm bg-background-primary px-8 py-3 text-base font-medium text-text-on-primary focus:outline-none sm:w-full"
                              )}
                            >
                              {data.personalizeConfirmButtonText}
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </Dialog>
        </Transition.Root>
      )}
      <div className="mx-auto max-w-2xl px-4 py-4 sm:px-6 sm:py-6 lg:max-w-7xl lg:px-8 lg:py-16">
        <div className="lg:grid lg:grid-cols-3 lg:items-start lg:gap-x-8">
          {/* Image gallery */}
          <div className="col-span-2 hidden grid-cols-2 gap-4 lg:grid">
            {Boolean(selectedVariant?.media?.length) &&
              [...(selectedVariant?.media || [])]
                .slice(0, 6)
                .map((media, index) => (
                  <MediaWrapper
                    key={index}
                    media={media}
                    divClassName="aspect-[1/1] rounded-sm"
                  />
                ))}
          </div>

          {/* Product info */}
          <div className="mt-2 px-4 sm:px-0 lg:mt-0">
            <div className="flex flex-wrap justify-between text-3xl text-text-primary lg:block">
              <h1 className="pb-2 pl-4 font-bold lg:pb-0 lg:pl-0">
                {productData.title || ""}
              </h1>

              <div className="pb-2 pl-4 lg:mt-3 lg:pb-0 lg:pl-0">
                <h2 className="sr-only">Price</h2>
                <p className="">
                  {getFancyPriceString(
                    selectedVariant?.presentmentPrices?.length &&
                      selectedVariant.presentmentPrices[0].price,
                    "de"
                  )}
                </p>
              </div>
            </div>

            {/* Mobile Images */}
            {Boolean(selectedVariant?.media?.length) && (
              <div className="mt-4 block lg:hidden">
                <SlickSlider dots>
                  {[...(selectedVariant?.media || [])]
                    .slice(0, 6)
                    .map((media, index) => (
                      <div key={index}>
                        <MediaWrapper
                          media={media}
                          divClassName="aspect-[1/1] w-full rounded-sm"
                        />
                      </div>
                    ))}
                </SlickSlider>
              </div>
            )}

            {/* Options */}
            <form className="mt-6">
              <div className="mx-auto flex flex-col space-y-4">
                {Boolean(productData.options?.length) &&
                  productData.options.map((option, index) => (
                    <div key={index}>
                      <div className="flex items-end justify-between">
                        <h3 className="text-base text-text-primary">
                          {`${option.name || `option_${index}`}${
                            data.optionsTitleSuffix
                              ? ` ${data.optionsTitleSuffix}`
                              : ""
                          }`}
                        </h3>
                        {data.sizeTableButtonText &&
                          productOptionsConfig?.sizeOptions.includes(
                            option.name
                          ) && (
                            <LinkWrapper
                              to="#"
                              className="text-sm text-text-secondary underline hover:text-text-primary"
                            >
                              {data.sizeTableButtonText}
                            </LinkWrapper>
                          )}
                      </div>
                      {productOptionsConfig?.colorOptions.includes(option.name)
                        ? radioGroupColor(option, index)
                        : radioGroupText(option, index)}
                    </div>
                  ))}
              </div>

              <div className="mx-auto">
                <div className="mt-10 flex flex-wrap justify-between lg:hidden">
                  <span>{productData.title || ""}</span>
                  <span>
                    {getFancyPriceString(
                      selectedVariant?.presentmentPrices?.length &&
                        selectedVariant.presentmentPrices[0].price,
                      productData.locale
                    )}
                  </span>
                </div>

                <div className="mt-1 flex sm:flex-col lg:mt-10">
                  <button
                    type="submit"
                    onClick={(event) => {
                      event.preventDefault();
                      addVariantToCart(
                        selectedVariant?.shopifyId,
                        1,
                        getCustomAttributes()
                      );
                    }}
                    disabled={
                      loading ||
                      nextMissingOptionName ||
                      selectedVariant?.inventoryQuantity <= 0
                    }
                    className={classNames(
                      nextMissingOptionName ||
                        selectedVariant?.inventoryQuantity <= 0
                        ? "opacity-40"
                        : "hover:bg-background-secondary",
                      "flex flex-1 items-center justify-center rounded-sm bg-background-primary px-8 py-3 text-base font-medium text-text-on-primary focus:outline-none sm:w-full"
                    )}
                  >
                    {(nextMissingOptionName &&
                      `${nextMissingOptionName} ${data.optionsTitleSuffix}`) ||
                      (selectedVariant?.inventoryQuantity <= 0 &&
                        data.outOfStockButtonText) ||
                      data.addToCartButtonText ||
                      "Add to cart"}
                  </button>
                </div>
                {isPersonalizable &&
                  data.personalizeButtonText &&
                  data.personalizeStatusTextTrue &&
                  data.personalizeStatusTextFalse && (
                    <div className="mt-4 flex justify-between">
                      <div className="flex text-text-primary">
                        <span>
                          {personalizeCustomText
                            ? data.personalizeStatusTextTrue
                            : data.personalizeStatusTextFalse}
                        </span>
                        {personalizeCustomText && (
                          <button
                            type="button"
                            onClick={() => {
                              setPersonalizeCustomText(null);
                            }}
                            className="ml-2"
                          >
                            <XCircleIcon className="h-4 w-4 hover:text-text-primary-light" />
                          </button>
                        )}
                      </div>
                      <button
                        type="button"
                        onClick={() => {
                          if (personalizeCustomText?.text) {
                            setPersonalizeCustomTextInput(
                              personalizeCustomText.text
                            );
                          } else {
                            setPersonalizeCustomTextInput(null);
                          }
                          if (personalizeCustomText?.font) {
                            setPersonalizeCustomTextFont(
                              personalizeCustomText.font
                            );
                          } else {
                            setPersonalizeCustomTextFont(
                              personalizeOptions?.length
                                ? personalizeOptions[0]
                                : null
                            );
                          }
                          setIsDialogOpen(true);
                        }}
                        className="text-sm text-text-secondary underline hover:text-text-primary"
                      >
                        {data.personalizeButtonText}
                      </button>
                    </div>
                  )}
              </div>
            </form>

            {/* description */}
            <section aria-labelledby="details-heading" className="mt-12">
              <h2 id="details-heading" className="sr-only">
                {data.additionalDetailsTitle || "Additional details"}
              </h2>

              {Boolean(descriptionChapters?.length) && (
                <div className="divide-y divide-divide-light border-t">
                  {descriptionChapters.map((chapter, index) => (
                    <Disclosure as="div" key={index}>
                      {({ open }) => (
                        <>
                          <h3>
                            <Disclosure.Button className="group relative flex w-full items-center justify-between py-6 text-left">
                              <span className="text-base font-semibold text-text-primary">
                                {chapter.title || ""}
                              </span>
                              <span className="ml-6 flex h-6 w-6 items-center">
                                {open ? (
                                  <MinusSmallIcon
                                    className="text-text-primary"
                                    aria-hidden="true"
                                  />
                                ) : (
                                  <PlusSmallIcon
                                    className="text-text-secondary group-hover:text-text-primary"
                                    aria-hidden="true"
                                  />
                                )}
                              </span>
                            </Disclosure.Button>
                          </h3>
                          <Disclosure.Panel
                            as="div"
                            className="prose prose-sm pb-6 text-text-secondary"
                          >
                            <span
                              dangerouslySetInnerHTML={{ __html: chapter.body }}
                            />
                          </Disclosure.Panel>
                        </>
                      )}
                    </Disclosure>
                  ))}
                </div>
              )}
            </section>
          </div>
        </div>
        {productData.customMetafields?.descriptionTitle && (
          <div>
            <div className="mx-auto max-w-2xl py-24 sm:py-32 lg:max-w-7xl">
              {/* Details section */}
              <section aria-labelledby="details-heading">
                <div className="flex flex-col items-center text-center">
                  <h2
                    id="details-heading"
                    className="text-3xl font-extrabold tracking-tight text-text-primary sm:text-4xl"
                  >
                    {productData.customMetafields.descriptionTitle}
                  </h2>
                  {productData.customMetafields?.descriptionTop && (
                    <MarkdownWrapper
                      text={productData.customMetafields.descriptionTop}
                      replaceParagraph={false}
                      className="mt-3 max-w-3xl text-lg text-text-secondary"
                    />
                  )}
                </div>

                <div className="mt-16 grid grid-cols-1 gap-y-16 lg:grid-cols-2 lg:gap-x-8">
                  <div>
                    <div className="aspect-[1/1] w-full overflow-hidden rounded-sm bg-background-secondary-light">
                      {selectedVariant?.media?.length >= 1 && (
                        <MediaWrapper
                          media={selectedVariant?.media[0]}
                          divClassName="h-full w-full"
                        />
                      )}
                    </div>
                    {productData.customMetafields?.descriptionLeft && (
                      <MarkdownWrapper
                        text={productData.customMetafields.descriptionLeft}
                        replaceParagraph={false}
                        className="mt-8 text-base text-text-secondary"
                      />
                    )}
                  </div>
                  <div>
                    <div className="aspect-[1/1] w-full overflow-hidden rounded-sm bg-background-secondary-light">
                      {selectedVariant?.media?.length >= 2 && (
                        <MediaWrapper
                          media={selectedVariant?.media[1]}
                          divClassName="h-full w-full"
                        />
                      )}
                    </div>
                    {productData.customMetafields?.descriptionRight && (
                      <MarkdownWrapper
                        text={productData.customMetafields.descriptionRight}
                        replaceParagraph={false}
                        className="mt-8 text-base text-text-secondary"
                      />
                    )}
                  </div>
                </div>
              </section>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

WithImageGrid.propTypes = {
  data: PropTypes.object,
};

export default WithImageGrid;
